import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';

import * as styles from './InventoryEventCatchUp.css';
import { useCatchUpModeEventCards } from './InventoryEventCatchUpBar.hooks';
import {
  InventoryEventCatchUpNextButton,
  InventoryEventCatchUpPrevButton,
} from './InventoryEventCatchUpBarButtons';
import { InventoryEventCatchUpCard } from './InventoryEventCatchUpCard';
import { InventoryEventCatchUpDropdown } from './InventoryEventCatchUpDropdown';

export function InventoryEventCatchUpBar() {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const { eventCards, selectedIndex, onNextClick, onPrevClick } =
    useCatchUpModeEventCards();

  useEffect(() => {
    if (virtuosoRef.current && selectedIndex) {
      // With virtuoso, it can happen that the selectedIndex is computed while the virtuoso is still rendering
      // Workaround to center the initial index by applying a short delay
      setTimeout(
        () =>
          virtuosoRef.current?.scrollToIndex({
            index: selectedIndex,
            behavior: 'smooth',
            align: 'center',
          }),
        100
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const showDropdown = useMemo(() => {
    return eventCards.length >= 10;
  }, [eventCards.length]);

  if (isEmpty(eventCards)) return null;

  return (
    <Stack direction="column" gap="s" className={styles.root}>
      <Stack direction="row" gap="m" width="full">
        <InventoryEventCatchUpPrevButton
          onPrevClickOverride={onPrevClick}
          numberOfEventOverride={eventCards.length}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: '125px', overflowY: 'hidden' }}
            totalCount={eventCards.length}
            data={eventCards}
            horizontalDirection
            itemContent={(_, props) => (
              <Stack
                width="full"
                alignItems="center"
                style={{ padding: `0 ${vars.spacing.xxs}` }}
              >
                <InventoryEventCatchUpCard {...props} />
              </Stack>
            )}
          />
        </div>

        <InventoryEventCatchUpNextButton
          onNextClickOverride={onNextClick}
          numberOfEventOverride={eventCards.length}
        />
        {showDropdown && (
          <InventoryEventCatchUpDropdown cardProps={eventCards} />
        )}
      </Stack>

      <div className={styles.numberContainer}>
        {selectedIndex != null && eventCards.length && (
          <span>{`${selectedIndex + 1} / ${eventCards.length}`}</span>
        )}
      </div>
    </Stack>
  );
}
